import * as React from 'react'
import { Check, ChevronDown, ChevronRight } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { Button } from './button'
import { cn } from '@/lib/utils'

export interface FilterMultiSelectGroup {
  name: string
  subfolders: string[]
}

interface FilterMultiSelectProps {
  /** Flat option list. Ignored when `groups` is provided. */
  options?: string[]
  /**
   * Main items with nested sub-items (e.g. folders with subfolders).
   * Only the main item shows by default; selecting/expanding it reveals
   * its sub-items indented beneath.
   */
  groups?: FilterMultiSelectGroup[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder: string
  icon?: React.ReactNode
  isLoading?: boolean
  isError?: boolean
  className?: string
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

export function FilterMultiSelect({
  options,
  groups,
  selected,
  onChange,
  placeholder,
  icon,
  isLoading,
  isError,
  className,
}: FilterMultiSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set())
  const rootRef = React.useRef<HTMLDivElement>(null)

  // `expanded` is separate from `selected` so a group can be browsed without
  // selecting it, but that means an external reset (e.g. "Reset All" calling
  // onChange([])) clears `selected` without ever touching `expanded` — the
  // group a user had opened stays expanded even though nothing is selected.
  React.useEffect(() => {
    if (selected.length === 0) setExpanded(new Set())
  }, [selected])

  React.useEffect(() => {
    if (!open) return

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return

      // Keep open when interacting with this filter trigger/content.
      if (rootRef.current?.contains(target)) return
      if (target.closest('[data-filter-multiselect-content]')) return

      setOpen(false)
    }

    document.addEventListener('pointerdown', handlePointerDown, true)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true)
    }
  }, [open])

  const label =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? cap(selected[0] ?? '')
        : `${cap(selected[0] ?? '')} +${selected.length - 1}`

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  const toggleExpanded = (name: string) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  const isExpanded = (group: FilterMultiSelectGroup) =>
    expanded.has(group.name) ||
    group.subfolders.some(sf => selected.includes(sf))

  const isEmpty = groups ? groups.length === 0 : (options ?? []).length === 0

  return (
    <div ref={rootRef} className='w-full min-w-0'>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type='button'
            variant='outline'
            disabled={isLoading || isError}
            className={cn(
              'h-9 w-full border-border hover:bg-accent/30 transition-colors text-xs rounded-lg justify-between px-3 font-normal',
              selected.length > 0 && 'border-primary/50 bg-primary/5',
              className
            )}
          >
            <div className='flex items-center gap-2 min-w-0'>
              {icon && (
                <span className='text-muted-foreground shrink-0'>{icon}</span>
              )}
              <span className='truncate text-left'>
                {isLoading ? 'Loading...' : isError ? 'Error' : label}
              </span>
            </div>
            <ChevronDown className='ml-2 h-3.5 w-3.5 shrink-0 opacity-50' />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          data-filter-multiselect-content=''
          className='w-[var(--radix-popover-trigger-width)] p-1 max-h-[280px] overflow-y-auto'
          align='start'
          onOpenAutoFocus={event => event.preventDefault()}
          onCloseAutoFocus={event => event.preventDefault()}
        >
          {isEmpty ? (
            <div className='py-4 text-center text-xs text-muted-foreground'>
              No options available
            </div>
          ) : (
            <>
              {selected.length > 0 && (
                <button
                  type='button'
                  onClick={() => onChange([])}
                  className='w-full flex items-center px-3 py-2 text-xs rounded-md hover:bg-accent transition-colors text-muted-foreground'
                >
                  Clear selection
                </button>
              )}
              {groups
                ? groups.map(group => (
                    <div key={group.name}>
                      <button
                        type='button'
                        onClick={() => {
                          toggle(group.name)
                          if (group.subfolders.length > 0) {
                            toggleExpanded(group.name)
                          }
                        }}
                        className={cn(
                          'w-full flex items-center gap-2 px-3 py-2 text-xs rounded-md hover:bg-accent transition-colors text-left',
                          selected.includes(group.name) &&
                            'bg-accent/50 text-primary font-medium'
                        )}
                      >
                        <div
                          className={cn(
                            'w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0',
                            selected.includes(group.name)
                              ? 'bg-primary border-primary'
                              : 'border-muted-foreground/30'
                          )}
                        >
                          {selected.includes(group.name) && (
                            <Check className='w-2.5 h-2.5 text-primary-foreground stroke-[3]' />
                          )}
                        </div>
                        <span className='truncate flex-1'>
                          {cap(group.name)}
                        </span>
                        {group.subfolders.length > 0 && (
                          <ChevronRight
                            className={cn(
                              'w-3 h-3 shrink-0 text-muted-foreground transition-transform',
                              isExpanded(group) && 'rotate-90'
                            )}
                          />
                        )}
                      </button>
                      {group.subfolders.length > 0 && isExpanded(group) && (
                        <div
                          ref={el =>
                            el?.scrollIntoView({
                              behavior: 'smooth',
                              block: 'nearest',
                            })
                          }
                          className='ml-4 border-l border-border pl-1'
                        >
                          {group.subfolders.map(sf => (
                            <button
                              type='button'
                              key={sf}
                              onClick={() => toggle(sf)}
                              className={cn(
                                'w-full flex items-center gap-2 px-3 py-2 text-xs rounded-md hover:bg-accent transition-colors text-left',
                                selected.includes(sf) &&
                                  'bg-accent/50 text-primary font-medium'
                              )}
                            >
                              <div
                                className={cn(
                                  'w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0',
                                  selected.includes(sf)
                                    ? 'bg-primary border-primary'
                                    : 'border-muted-foreground/30'
                                )}
                              >
                                {selected.includes(sf) && (
                                  <Check className='w-2.5 h-2.5 text-primary-foreground stroke-[3]' />
                                )}
                              </div>
                              <span className='truncate'>{cap(sf)}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                : (options ?? []).map(option => (
                    <button
                      type='button'
                      key={option}
                      onClick={() => toggle(option)}
                      className={cn(
                        'w-full flex items-center gap-2 px-3 py-2 text-xs rounded-md hover:bg-accent transition-colors text-left',
                        selected.includes(option) &&
                          'bg-accent/50 text-primary font-medium'
                      )}
                    >
                      <div
                        className={cn(
                          'w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0',
                          selected.includes(option)
                            ? 'bg-primary border-primary'
                            : 'border-muted-foreground/30'
                        )}
                      >
                        {selected.includes(option) && (
                          <Check className='w-2.5 h-2.5 text-primary-foreground stroke-[3]' />
                        )}
                      </div>
                      <span className='truncate'>{cap(option)}</span>
                    </button>
                  ))}
            </>
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}
